import {
  saveApplicationDraft,
  updateApplicationDraft
} from "../services/applicationService"

import {
  createMark,
  updateMark,
  deleteMark
} from "../services/markService"

import {
  createGoodsAndServices,
  updateGoodsAndServices,
  deleteGoodsAndServices
} from "../services/goodsAndServicesService"

import {
  createPriority,
  updatePriority,
  deletePriority
} from "../services/priorityService"

import {
  createSeniority,
  updateSeniority,
  deleteSeniority
} from "../services/seniorityService"

import {
  createSignature,
  updateSignature,
  deleteSignature
} from "../services/signatureService"

import {
  createApplicant,
  updateApplicant,
  deleteApplicant
} from "../services/applicantService"

import { MarkData } from "../components/trademark/mark/MarkSection"

interface GoodsAndServicesUiEntry {
  classNumber: number
  text: string
}

interface GoodsAndServicesApiEntry {
  classNumber: number
  language: string
  terms: string[]
}

const FIXED_LANGUAGES = {
  filingLanguage: "English",
  secondLanguage: "German"
}

function convertToApiFormat(
  list: GoodsAndServicesUiEntry[]
): GoodsAndServicesApiEntry[] {
  const grouped: Record<number, string[]> = {}
  for (const item of list) {
    if (!grouped[item.classNumber]) grouped[item.classNumber] = []
    grouped[item.classNumber].push(item.text)
  }
  return Object.entries(grouped).map(([classNumber, terms]) => ({
    classNumber: Number(classNumber),
    language: "en",
    terms
  }))
}

function toIsoOrNull(value: string | null | undefined) {
  if (!value) return null
  const d = new Date(value)
  return isNaN(d.getTime()) ? null : d.toISOString()
}

export async function saveDraft(params: {
  token: string
  referenceNumber: string
  languages?: any
  markData: MarkData | null
  goodsAndServices?: GoodsAndServicesUiEntry[] | null
  applicants?: any[] | null
  priorityData?: any[] | null
  seniorityData?: any[] | null
  signature?: any | null
  draftId?: string | null
  markId?: string | null
  goodsAndServicesId?: string | null
  signatureId?: string | null
  onDraftSaved?: (id: string) => void
  onMarkSaved?: (id: string | null) => void
  onGoodsAndServicesSaved?: (id: string | null) => void
  onPrioritiesSaved?: (ids: string[] | null) => void
  onSenioritiesSaved?: (ids: string[] | null) => void
  onSignatureSaved?: (id: string | null) => void
}): Promise<string> {
  const {
    token,
    referenceNumber,
    markData,
    goodsAndServices,
    applicants,
    priorityData,
    seniorityData,
    signature,
    draftId,
    markId,
    goodsAndServicesId,
    signatureId,
    onDraftSaved,
    onMarkSaved,
    onGoodsAndServicesSaved,
    onPrioritiesSaved,
    onSenioritiesSaved,
    onSignatureSaved
  } = params

  if (!token) throw new Error("No auth token")

  let savedMarkId = markId || null
  let savedGoodsAndServicesId = goodsAndServicesId || null
  let savedPriorityIds: string[] = []
  let savedSeniorityIds: string[] = []
  let savedSignatureId = signatureId || null
  let savedApplicantIds: string[] = []

  const languages = FIXED_LANGUAGES

  if (!markData && savedMarkId) {
    await deleteMark(savedMarkId)
    savedMarkId = null
    onMarkSaved?.(null)
  }

  if (markData) {
    const markPayload: any = {
      type: markData.type,
      word: markData.word ?? "",
      text: markData.text ?? "",
      colorClaim: markData.colorClaim ?? "",
      category: markData.category
    }

    if (markData.euipoDocumentId) {
      markPayload.euipoDocumentId = markData.euipoDocumentId
    }

    if (markData.imageData) {
      markPayload.imageData = markData.imageData
    }

    if (markData.contentType) {
      markPayload.contentType = markData.contentType
    }

    if (savedMarkId) {
      const updated = await updateMark(savedMarkId, markPayload)
      savedMarkId = updated._id
    } else {
      const created = await createMark(markPayload)
      savedMarkId = created._id
    }

    onMarkSaved?.(savedMarkId)
  }

  const hasGoods =
    goodsAndServices &&
    Array.isArray(goodsAndServices) &&
    goodsAndServices.length > 0

  const goodsApi = hasGoods ? convertToApiFormat(goodsAndServices!) : []

  if (!hasGoods) {
    if (savedGoodsAndServicesId) {
      await deleteGoodsAndServices(savedGoodsAndServicesId)
    }
    savedGoodsAndServicesId = null
    onGoodsAndServicesSaved?.(null)
  } else {
    if (savedGoodsAndServicesId) {
      const updated = await updateGoodsAndServices(
        savedGoodsAndServicesId,
        goodsApi
      )
      savedGoodsAndServicesId = updated._id
    } else {
      const created = await createGoodsAndServices(goodsApi)
      savedGoodsAndServicesId = created._id
    }
    onGoodsAndServicesSaved?.(savedGoodsAndServicesId)
  }

  if (priorityData && Array.isArray(priorityData)) {
    for (const priority of priorityData) {
      const filingDateIso = toIsoOrNull(priority.filingDate)

      if (!priority._id && !priority.isDeleted) {
        const created = await createPriority({
          office: priority.office,
          filingNumber: priority.filingNumber,
          filingDate: filingDateIso,
          euipoDocumentId: priority.euipoDocumentId,
          originalFileName: priority.originalFileName
        })
        savedPriorityIds.push(created._id)
        Object.assign(priority, created, { isDeleted: false })
        continue
      }

      if (priority._id && priority.isDeleted) {
        await deletePriority(priority._id)
        continue
      }

      if (priority._id && !priority.isDeleted) {
        const updated = await updatePriority(priority._id, {
          office: priority.office,
          filingNumber: priority.filingNumber,
          filingDate: filingDateIso,
          euipoDocumentId: priority.euipoDocumentId,
          originalFileName: priority.originalFileName
        })
        savedPriorityIds.push(updated._id)
        Object.assign(priority, updated)
      }
    }
    onPrioritiesSaved?.(savedPriorityIds)
  }

  if (seniorityData && Array.isArray(seniorityData)) {
    for (const seniority of seniorityData) {
      const payload = {
        type: seniority.type,
        countryCode: seniority.countryCode,
        applicationDate: toIsoOrNull(seniority.applicationDate),
        applicationNumber: seniority.applicationNumber,
        registrationDate: toIsoOrNull(seniority.registrationDate),
        registrationNumber: seniority.registrationNumber,
        priorityDate: toIsoOrNull(seniority.priorityDate),
        st13ApplicationNumber: null,
        st13ApplicationIR: null,
        euipoDocumentId: seniority.euipoDocumentId,
        originalFileName: seniority.originalFileName
      }

      if (!seniority._id && !seniority.isDeleted) {
        const created = await createSeniority(payload)
        savedSeniorityIds.push(created._id)
        Object.assign(seniority, created, { isDeleted: false })
        continue
      }

      if (seniority._id && seniority.isDeleted) {
        await deleteSeniority(seniority._id)
        continue
      }

      if (seniority._id && !seniority.isDeleted) {
        const updated = await updateSeniority(seniority._id, payload)
        savedSeniorityIds.push(updated._id)
        Object.assign(seniority, updated)
      }
    }
    onSenioritiesSaved?.(savedSeniorityIds)
  }

  const hasSignature =
    signature &&
    signature.accepted &&
    signature.signerName &&
    signature.signerCapacity

  if (!hasSignature) {
    if (savedSignatureId) await deleteSignature(savedSignatureId)
    savedSignatureId = null
    onSignatureSaved?.(null)
  } else {
    if (savedSignatureId) {
      const updated = await updateSignature(savedSignatureId, {
        signerName: signature.signerName,
        signerCapacity: signature.signerCapacity
      })
      savedSignatureId = updated._id
    } else {
      const created = await createSignature({
        signerName: signature.signerName,
        signerCapacity: signature.signerCapacity
      })
      savedSignatureId = created._id
    }
    onSignatureSaved?.(savedSignatureId)
  }

  if (applicants && Array.isArray(applicants)) {
    for (const applicant of applicants) {
      if (applicant._id && applicant.isDeleted) {
        await deleteApplicant(applicant._id)
        continue
      }

     if (applicant._id && !applicant.isDeleted) {
        const updated = await updateApplicant(applicant._id, applicant)
        savedApplicantIds.push(updated._id)
        continue
        }

      if (!applicant._id && !applicant.isDeleted) {
        const created = await createApplicant(applicant)
        savedApplicantIds.push(created._id)
        applicant._id = created._id
      }
    }
  }

  const payload = {
    referenceNumber,
    languages: {
      filingLanguage: languages.filingLanguage,
      secondLanguage: languages.secondLanguage
    },
    markId: savedMarkId,
    goodsAndServices: savedGoodsAndServicesId,
    applicants: savedApplicantIds,
    priorities: savedPriorityIds,
    seniorities: savedSeniorityIds,
    signature: savedSignatureId,
    representative: {
      name: "DANTZ IP & LAW",
      street: "Kurfürstendamm 43D",
      postalCode: "10719",
      city: "Berlin",
      country: "Deutschland",
      countryShort: "DE"
    }
  }

  if (draftId) {
    await updateApplicationDraft(draftId, payload)
    return draftId
  } else {
    const created = await saveApplicationDraft(payload)
    onDraftSaved?.(created._id)
    return created._id
  }
}

